import { useDebouncedCallback } from 'hooks/useDebounce';
import { useAppSelector } from 'modules/Editor/store';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { createContext, useContext, useEffect, useRef } from 'react';
import { WidgetId } from 'types/idTypes';
import { pick } from 'utils/pick';
import { proxy, useSnapshot } from 'valtio';

import { MULTI_WIDGET_TOOLBAR_CONFIG } from '../../../Widget.config';
import { getWidgetTypeFromId } from '../../../Widget.helpers';
import { MultiEditOption, MultiEditWidgetTypes } from '../../../Widget.types';
import { useMultiEditConfig } from '../../../store/MultiEditConfigProvider';
import { getToolbarOptions } from './MultiWidgetToolbar.helpers';

// Keeps all the selected widget values for each option in an array
type MultiWidgetValues = { [Option in MultiEditOption]: any[] };

const defaultStoreState = {
  [MultiEditOption.Color]: [],
  [MultiEditOption.TextColor]: [],
  [MultiEditOption.BorderColor]: [],
  [MultiEditOption.BorderStyle]: [],
  [MultiEditOption.BorderWidth]: [],
  [MultiEditOption.FontSize]: [],
  [MultiEditOption.FontFamily]: [],
  [MultiEditOption.FontWeight]: [],
  [MultiEditOption.FontStyle]: [],
  [MultiEditOption.TextDecoration]: [],
  [MultiEditOption.TextAlign]: [],
  [MultiEditOption.BasicShapeType]: [],
};

const MultiWidgetValueContext = createContext<MultiWidgetValues>(defaultStoreState);

export const MultiWidgetValueProvider = ({ children }: { children: React.ReactNode }) => {
  const { config, getCustomDisplayFunction, getCustomKey, getCustomValueFunction } = useMultiEditConfig();

  const widgets = useAppSelector(selectWidgets);
  const activeWidgets = useAppSelector(selectActiveWidgets);

  // proxy state to make it reactive as the toolbar is a bit performance sensitive and to make it easier to update
  const state = useRef<MultiWidgetValues>(proxy(defaultStoreState)).current;

  /**
   * Combines the values of the active widgets into the state for the multi widget toolbar
   * E.g. widget 1: { color: 'red' }, widget 2: { color: 'blue' } -> state: { color: ['red', 'blue'] }
   */
  const combineWidgetValues = () => {
    if (!activeWidgets || activeWidgets.length < 2) return;

    // get the current options for the multi widget toolbar (e.g. ['color', 'borderColor', 'borderWidth'])
    const options = getToolbarOptions(activeWidgets);

    // iterate over the options and get the widget values for each option
    for (let i = 0; i < options.length; i++) {
      const option = options[i];

      // reset the option value
      state[option] = [];

      // loop through all active widgets and add their values to the state
      for (let j = 0; j < activeWidgets.length; j++) {
        const activeWidget = activeWidgets[j];
        const widgetId: WidgetId = activeWidget.id;

        const processWidget = (widgetId: WidgetId) => {
          if (!config[widgetId]) return;

          // if the active widget is not a multi select widget skip it
          const widgetType = getWidgetTypeFromId(widgetId);
          if (!MULTI_WIDGET_TOOLBAR_CONFIG[widgetType as MultiEditWidgetTypes]) return;

          // if the widget doesn't have the option skip it
          if (!MULTI_WIDGET_TOOLBAR_CONFIG[widgetType as MultiEditWidgetTypes].includes(option)) return;

          const displayMultiSelectFn = getCustomDisplayFunction(widgetId);
          if (typeof displayMultiSelectFn === 'function' && !displayMultiSelectFn()) return;

          const getValue = getCustomValueFunction(widgetId, option);
          const key = getCustomKey(widgetId, option) || option;
          const optionValue = getValue ? getValue(widgets[widgetId]) : pick(widgets[widgetId], key);
          state[option].push(optionValue);
        };

        processWidget(widgetId);
      }
    }
  };

  // this is debounced a little bit as an optimization since it runs after all widget updates
  const debounceSyncStore = useDebouncedCallback(combineWidgetValues, 25);

  // sync combined the toolbar values for the widgets with the widget values
  useEffect(debounceSyncStore, [widgets, activeWidgets.length, debounceSyncStore]);

  return <MultiWidgetValueContext.Provider value={state}>{children}</MultiWidgetValueContext.Provider>;
};

export const useMultiWidgetValue = () => {
  const multiWidgetValueContext = useContext(MultiWidgetValueContext);
  const multiWidgetValue = useSnapshot(multiWidgetValueContext);

  return multiWidgetValue;
};
